import {
  BookOpen,
  ChevronRight,
  FileText,
  FileWarning,
  FolderKanban,
  Loader2,
  Receipt,
  Search,
  UserCog,
  Users,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { globalSearch } from "@/features/search/api";
import { resultHref } from "@/features/search/result-href";
import { SEARCH_GROUPS, type SearchHit, type SearchResults } from "@/features/search/types";

const MIN_LEN = 2;

const GROUP_META: Record<keyof SearchResults, { label: string; icon: LucideIcon }> = {
  clients: { label: "Clients", icon: Users },
  cases: { label: "Cases", icon: FolderKanban },
  documents: { label: "Documents", icon: FileText },
  invoices: { label: "Invoices", icon: Receipt },
  notices: { label: "IRS Notices", icon: FileWarning },
  practitioners: { label: "Practitioners", icon: UserCog },
  kb: { label: "Help & Knowledge Base", icon: BookOpen },
};

function ResultRow({ hit }: { hit: SearchHit }) {
  return (
    <Link
      to={resultHref(hit)}
      className="hover:bg-muted flex items-center gap-3 px-4 py-3 transition-colors"
    >
      <span className="min-w-0 flex-1">
        <span className="text-foreground block truncate text-sm font-medium">{hit.title}</span>
        {hit.subtitle && (
          <span className="text-muted-foreground block truncate text-xs">{hit.subtitle}</span>
        )}
      </span>
      <ChevronRight className="text-muted-foreground size-4 shrink-0" />
    </Link>
  );
}

/** Firm-wide search: a centered hero input + results grouped by entity type.
 * The query lives in the URL (`?q=`) so a search is linkable/back-navigable. */
export function GlobalSearch() {
  const [params, setParams] = useSearchParams();
  // Local input state is the source of truth (avoids per-keystroke lag from a
  // URL round-trip); seed it from `?q=` so /search?q=term still deep-links.
  const [q, setQ] = useState(() => params.get("q") ?? "");
  const [results, setResults] = useState<SearchResults | null>(null);
  const [loading, setLoading] = useState(false);

  function onChange(value: string) {
    setQ(value);
    setParams(value ? { q: value } : {}, { replace: true });
  }

  // Debounced lookup; drop stale responses that resolve after a newer keystroke.
  useEffect(() => {
    const term = q.trim();
    if (term.length < MIN_LEN) {
      setResults(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    let active = true;
    const id = setTimeout(() => {
      globalSearch(term)
        .then((r) => active && setResults(r))
        .catch(() => active && setResults(null))
        .finally(() => active && setLoading(false));
    }, 250);
    return () => {
      active = false;
      clearTimeout(id);
    };
  }, [q]);

  const total = results
    ? SEARCH_GROUPS.reduce((sum, key) => sum + results[key].length, 0)
    : 0;
  const hasQuery = q.trim().length >= MIN_LEN;

  return (
    <div className="mx-auto w-full max-w-3xl">
      <div className="pt-6 pb-8 text-center">
        <h1 className="text-foreground text-2xl font-semibold">Search</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Find clients, documents, cases, invoices, notices, practitioners, and help articles.
        </p>
      </div>

      <div className="relative">
        <Search className="text-muted-foreground absolute top-1/2 left-4 size-5 -translate-y-1/2" />
        {loading && (
          <Loader2 className="text-muted-foreground absolute top-1/2 right-4 size-5 -translate-y-1/2 animate-spin" />
        )}
        <Input
          autoFocus
          value={q}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Search everything…"
          aria-label="Search everything"
          className="h-12 rounded-full pr-12 pl-12 text-base"
        />
      </div>

      <div className="mt-6 pb-16">
        {!hasQuery ? (
          <p className="text-muted-foreground py-10 text-center text-sm">
            Type at least {MIN_LEN} characters to search.
          </p>
        ) : loading && !results ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full rounded-lg" />
            ))}
          </div>
        ) : total === 0 ? (
          <p className="text-muted-foreground py-10 text-center text-sm">
            No results for “{q.trim()}”.
          </p>
        ) : (
          <div className="space-y-6">
            {SEARCH_GROUPS.map((key) => {
              const hits = results?.[key] ?? [];
              if (!hits.length) return null;
              const { label, icon: Icon } = GROUP_META[key];
              return (
                <section key={key}>
                  <div className="text-muted-foreground mb-2 flex items-center gap-2 px-1 text-xs font-semibold tracking-wide uppercase">
                    <Icon className="size-4" />
                    {label}
                    <span className="text-muted-foreground/70">({hits.length})</span>
                  </div>
                  <Card className="gap-0 divide-y overflow-hidden p-0">
                    {hits.map((hit) => (
                      <ResultRow key={`${hit.type}-${hit.id}`} hit={hit} />
                    ))}
                  </Card>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
